import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import StyledFooterMainUniversal from './FooterMainUniversal.styled';
import SVGIcon from '../utility/SVGIcon';
import { storeName } from '../../config/constants';
import { openExternalLinksInNewTab } from '../../helpers/externalLinks';
import HtmlContent from '../HtmlContent/HtmlContent';
import { ThemePramsContext } from '../../App';

// Builds a url out of a config pattern, e.g. '/dispensary/{uri}'
const buildUrl = (pattern, uri) => (pattern || '').replace('{uri}', uri);

/**
 * One footer for every marketplace. The markup is the rebud one (FooterMain);
 * the logo, labels, links, socials and fonts come from footerConfig of the
 * selected market config.
 */
const FooterMainUniversal = React.memo((props) => {
  const {
    license_number,
    simple,
    pickupLocations,
    deliveryAreas,
    footer,
    message,
    onSubscribe,
    brandLogo,
    seoEnabled,
    seoReady,
    menuAvailability,
  } = props;
  const { footerConfig = {} } = useContext(ThemePramsContext);
  const {
    classNames = {},
    brand = {},
    socials = [],
    columns = [],
    locations = {},
    deliveryAreas: deliveryAreasConfig = {},
    newsletter = {},
    license = {},
    bottom = {},
    styling = {},
  } = footerConfig;

  // Stores with seo turned off get the short footer: blocks and links marked
  // seoOnly are hidden
  const seoOff = seoEnabled === false;
  const isVisible = (item) => !(seoOff && item?.seoOnly);

  const currentYear = new Date().getFullYear();
  const emailRef = useRef();

  useEffect(() => {
    if (message === 'Success' && emailRef.current) {
      emailRef.current.value = '';
    }
  }, [message]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (onSubscribe) {
      onSubscribe({ email: e.target.email.value.trim() });
    }
  };

  const handleSamePageClick = (to) => () => {
    if (window.location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderBrand = () => {
    const logoSrc = brand.useBrandLogo ? brandLogo : brand.img;
    if (logoSrc) {
      return <img src={logoSrc} className="footer-brand__logo" alt={`${storeName} logo`} />;
    }
    if (brand.icon) {
      return <SVGIcon name={brand.icon} className="footer-brand__logo" />;
    }
    return (
      <h2 className={`footer-brand__name ${classNames.brandName || ''}`}>{(brand.text || storeName).toUpperCase()}</h2>
    );
  };

  const bottomBar = (
    <div className="footer-bottom">
      <p className={`footer-bottom__copy ${classNames.copy || ''}`}>
        &copy; {currentYear} {bottom.copyName || storeName}. All rights reserved.
      </p>
      <div className="footer-bottom__links">
        {(bottom.links || []).map((item) => (
          <Link key={item.text} to={item.to} className={classNames.copy || ''}>
            {item.text}
          </Link>
        ))}
      </div>
    </div>
  );

  // Wait for /stores/info: drawing the full footer first and trimming it once
  // the flag arrives makes the links visibly disappear
  if (!seoReady) return null;

  if (simple) {
    return (
      <StyledFooterMainUniversal simple themeProps={styling} data-testid="footerMain">
        {bottomBar}
      </StyledFooterMainUniversal>
    );
  }

  return (
    <StyledFooterMainUniversal themeProps={styling} data-testid="footerMain">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-col footer-col--brand">
            <Link to="/">{renderBrand()}</Link>
            {footer?.about ? (
              <HtmlContent
                className={`footer-brand__about ${classNames.about || ''}`}
                html={openExternalLinksInNewTab(footer.about)}
              />
            ) : (
              <p className={`footer-brand__about ${classNames.about || ''}`}>{brand.aboutFallback}</p>
            )}
            {socials.length > 0 && (
              <div className="footer-brand__socials">
                {socials.map((social) => {
                  const href = social.href || (footer && footer[social.urlKey]);
                  if (!href) return null;
                  return (
                    <a
                      key={social.icon}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-social-link"
                    >
                      <span className="t-hidden">{social.label}</span>
                      <SVGIcon name={social.icon} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {columns.map((column) => (
            <div className="footer-col" key={column.heading}>
              <h4 className={`footer-col__heading ${classNames.heading || ''}`}>{column.heading}</h4>
              <ul className="footer-col__list">
                {(column.links || []).filter(isVisible).map((item) => (
                  <li key={item.text}>
                    {item.href ? (
                      <a href={item.href} className={classNames.link || ''}>
                        {item.text}
                      </a>
                    ) : (
                      <Link to={item.to} className={classNames.link || ''}>
                        {item.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {locations.visible && isVisible(locations) && menuAvailability?.dispensaries !== false && pickupLocations && pickupLocations.length > 0 && (
            <div className="footer-col">
              <h4 className={`footer-col__heading ${classNames.heading || ''}`}>{locations.heading}</h4>
              <ul className="footer-col__list">
                {pickupLocations.map((loc, i) => (
                  <li key={i} className="footer-col__location">
                    <Link to={buildUrl(locations.urlPattern, loc.uri_param)} className={classNames.link || ''}>
                      {loc.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {deliveryAreasConfig.visible && isVisible(deliveryAreasConfig) && menuAvailability?.deliveryAreas !== false && deliveryAreas && deliveryAreas.length > 0 && (
          <>
            <hr className="footer-divider" />
            <div className="footer-delivery-areas">
              <h4 className={`footer-col__heading ${classNames.heading || ''}`}>{deliveryAreasConfig.heading}</h4>
              <ul className="footer-delivery-areas__list">
                {deliveryAreas.map((area) => {
                  const to = buildUrl(deliveryAreasConfig.urlPattern, area.uri_param);
                  return (
                    <li key={area.uri_param} className="footer-delivery-areas__item">
                      <Link
                        to={to}
                        title={area.title}
                        onClick={handleSamePageClick(to)}
                        className={classNames.link || ''}
                      >
                        {area.city_name}
                      </Link>
                    </li>
                  );
                })}
                <li className="footer-delivery-areas__item footer-delivery-areas__item--all">
                  <Link
                    to={deliveryAreasConfig.allTo}
                    onClick={handleSamePageClick(deliveryAreasConfig.allTo)}
                    className={classNames.link || ''}
                  >
                    {deliveryAreasConfig.allText}
                  </Link>
                </li>
              </ul>
            </div>
          </>
        )}

        <hr className="footer-divider" />

        <div className="footer-mid">
          {newsletter.visible && (
            <div className="footer-newsletter">
              <h4 className={`footer-col__heading ${classNames.heading || ''}`}>{newsletter.heading}</h4>
              <p className={`footer-newsletter__copy ${classNames.copy || ''}`}>{newsletter.copy}</p>
              <form className="footer-newsletter__form" onSubmit={handleSubscribe}>
                <label className="t-hidden" htmlFor="footerEmail">
                  Enter your email address
                </label>
                <input
                  className="footer-newsletter__input"
                  id="footerEmail"
                  name="email"
                  type="email"
                  required
                  placeholder={newsletter.placeholder}
                  ref={emailRef}
                />
                <button type="submit" className={`footer-newsletter__btn ${classNames.button || ''}`}>
                  {newsletter.buttonText}
                </button>
              </form>
            </div>
          )}

          {license.visible && license_number && (
            <div className="footer-license">
              <h4 className={`footer-col__heading ${classNames.heading || ''}`}>{license.heading}</h4>
              <p className={`footer-license__text ${classNames.copy || ''}`}>{license_number}</p>
            </div>
          )}
        </div>

        <hr className="footer-divider" />

        {bottomBar}
      </div>
    </StyledFooterMainUniversal>
  );
});

FooterMainUniversal.propTypes = {
  license_number: PropTypes.string,
  simple: PropTypes.bool,
  brandLogo: PropTypes.string,
  pickupLocations: PropTypes.array,
  deliveryAreas: PropTypes.arrayOf(
    PropTypes.shape({
      city_name: PropTypes.string,
      title: PropTypes.string,
      uri_param: PropTypes.string,
    })
  ),
  footer: PropTypes.shape({
    about: PropTypes.string,
    twitter_url: PropTypes.string,
    instagram_url: PropTypes.string,
  }),
  message: PropTypes.string,
  onSubscribe: PropTypes.func,
  seoEnabled: PropTypes.bool,
  seoReady: PropTypes.bool,
  menuAvailability: PropTypes.object,
};

export default FooterMainUniversal;
